import { Product } from '../types';

/** Estrae il numero a 3 cifre che precede il nome nelle etichette NTS
 * (es. "040 - Toner Refill" → 40). Più basso = più priorità. */
function labelPrefixNum(label: string | number | null | undefined): number {
  if (label === null || label === undefined) return 999;
  const match = String(label).trim().match(/^(\d+)/);
  if (!match) return 999;
  const n = parseInt(match[1], 10);
  return Number.isNaN(n) ? 999 : n;
}

/**
 * Ordina i prodotti replicando ESATTAMENTE la gerarchia calcolata dal
 * backend (vedi ``_products_sort_pipeline_stages`` in
 * ``backend/products/router.py``), per evitare che questo sort lato
 * client sovrascriva l'ordinamento corretto con uno diverso:
 *
 * 1) Toner (sottogruppo merceologico "030 - Toner") SEMPRE primo in
 *    assoluto, indipendentemente dal gruppo merceologico di appartenenza.
 * 2) Tutti gli altri articoli: numero di sottogruppo merceologico
 *    crescente → numero di famiglia prodotto crescente → tipologia
 *    (Originale prima del resto).
 *
 * Nota: NON considera il gruppo merceologico (es. "640 -
 * Toner/Cartuccia Toner") — solo il sottogruppo, per scelta esplicita
 * dell'utente (09/07/2026).
 *
 * Prodotti senza enrichment ERP (seed/demo) finiscono in coda (999) su
 * ciascun livello, poi tiebreaker su SKU (come nel backend).
 */
export function sortProducts(products: Product[]): Product[] {
  return [...products].sort((a, b) => {
    const sgA = labelPrefixNum(a.erp?.detail?.sottogruppo?.label);
    const sgB = labelPrefixNum(b.erp?.detail?.sottogruppo?.label);
    const fA = labelPrefixNum(a.erp?.detail?.famigliaProdotto?.label);
    const fB = labelPrefixNum(b.erp?.detail?.famigliaProdotto?.label);

    // 0 = toner (sottogruppo 030), 1 = tutto il resto
    const isTonerA = sgA === 30 ? 0 : 1;
    const isTonerB = sgB === 30 ? 0 : 1;
    if (isTonerA !== isTonerB) return isTonerA - isTonerB;

    if (sgA !== sgB) return sgA - sgB;
    if (fA !== fB) return fA - fB;

    // Tipologia: Originale prima, il resto dopo
    const tA = a.type === 'Originale' ? 0 : 1;
    const tB = b.type === 'Originale' ? 0 : 1;
    if (tA !== tB) return tA - tB;

    // Tiebreaker: SKU (stesso criterio finale del backend)
    return a.sku.localeCompare(b.sku);
  });
}
